import { useState } from "react";
import {
  EditOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Row,
  Table,
  Tag,
  notification,
  Typography,
  Popconfirm,
  Space
} from "antd";
import { ColumnsType } from "antd/es/table";
import {
  useGetBrandModelQuery,
  useUpdatePricingMutation,
  useCreatePricingMutation,
  useDeletePricingMutation
} from "../../redux/services/brandModelApi";
import { localizeDate, isUserHasRoleTypes } from "../../utils";
import AddPricingInBrandModelModal from "./AddPricingInBrandModelModal";
import ErrorDetails from "../../components/ErrorDetails";
import { useNavigate, useParams } from "react-router-dom";

const { Text } = Typography;
const ViewBrandModel = (props: any) => {
  const params = useParams();
  const navigate = useNavigate();

  const modelId = params?.modelId;

  const [api, contextHolder] = notification.useNotification();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState({});
  const [selectedPricingId, setSelectedPricingId] = useState(null);
  const [isEditMode, setEditMode] = useState(false);
  const { data, error, isLoading, refetch } = useGetBrandModelQuery(modelId);
  const brandModel = data?.brand_model;
  const [createPricing] = useCreatePricingMutation();

  const [updatePricing] = useUpdatePricingMutation();

  const [deletePricing] = useDeletePricingMutation();

  const onGoBack = () => {
    navigate(-1);
  };

  const onUpdatePricing = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await updatePricing(values).unwrap();
      api.success({
        message: "Update model successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      setEditMode(false);

      refetch();
    } catch (error: any) {
      api.info({
        message: "Update model failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onRemove = async (pricing: any) => {
    const pricingId = pricing?.id;
    setSelectedPricingId(pricingId);
    try {
      setLoading(true);
      await deletePricing({ modelId, pricingId }).unwrap();
      api.success({
        message: "Delete pricing successful",
        placement: "top"
      });
      refetch();
      setLoading(false);
    } catch (error: any) {
      api.info({
        message: "Delete pricing failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdate = (pricing: any) => {
    setSelectedPricing(pricing);
    setShowModal(true);
    setEditMode(true);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Pricing per meter",
      dataIndex: "pricing_per_meter",
      key: "pricing_per_meter"
    },
    {
      title: "Complementary cable length",
      dataIndex: "complementary_cable_length",
      key: "complementary_cable_length"
    },
    {
      title: "Charger Type",
      dataIndex: "charger_size",
      key: "charger_size"
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
      render: (start_date) => <Text key={start_date}>{localizeDate(start_date)}</Text>
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      render: (end_date) => <Text key={end_date}>{localizeDate(end_date)}</Text>
    },
    {
      title: "Action",
      key: "action",
      render: (_, pricing) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => onUpdate(pricing)} title="Update"></Button>
          <Popconfirm
            title="Are you sure you want to delete pricing"
            okText="Delete"
            placement="bottom"
            cancelText="Cancel"
            onConfirm={(event) => {
              onRemove(pricing);
            }}
            onCancel={(event: any) => event.stopPropagation()}
          >
            <Button
              icon={<DeleteOutlined />}
              title="remove"
              loading={selectedPricingId == pricing?.id ? loading : false}
            ></Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const addPricing = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await createPricing(values).unwrap();
      api.success({
        message: "Create pricing successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create pricing failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {!isLoading && brandModel && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Model Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>

          {brandModel?.brandModel_logo ? (
            <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 20 }}>
              <div style={{ height: "50px" }}>
                {brandModel?.brandModel_logo ? (
                  <img src={brandModel?.brandModel_logo} alt="no-img" height="50px" />
                ) : (
                  "-"
                )}
              </div>
              <h2>
                {brandModel?.name} ({brandModel?.code})
              </h2>
            </div>
          ) : (
            ""
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{brandModel?.name}</Descriptions.Item>
            <Descriptions.Item label="Code">{brandModel?.code || "-"}</Descriptions.Item>
            <Descriptions.Item label="Charger type">
              {brandModel?.charger_size || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={brandModel?.status === "active" ? "green" : "red"}>
                {brandModel?.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Pricings</h2>
              <Button
                size="middle"
                onClick={() => setShowModal(!showModal)}
                icon={<PlusCircleOutlined />}
                type="default"
              >
                Add Pricing
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            bordered
            columns={columns}
            pagination={false}
            dataSource={brandModel?.pricings}
            scroll={{ x: 400 }}
          />

          <AddPricingInBrandModelModal
            isModalOpen={showModal}
            brandModel={brandModel}
            onCancel={() => {
              setShowModal(false);
              setEditMode(false);
            }}
            onUpdatePricing={isEditMode && onUpdatePricing}
            isEditMode={isEditMode}
            selectedPricing={isEditMode ? selectedPricing : {}}
            addPricing={!isEditMode && addPricing}
          />
        </>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
    </>
  );
};

export default ViewBrandModel;
